/**
 * 权限管理工具类
 * 用于管理当前用户的权限信息
 */

import storage from '@/storage/storage'
import { getCurrentUserPermits } from '@/util/api'

const PERMISSIONS_KEY = 'user_permissions'
const IS_SUPER_ADMIN_KEY = 'is_super_admin'
const ROLE_ID_KEY = 'user_role_id'

/**
 * 获取当前用户的权限列表
 */
export const getUserPermissions = () => {
  const permissions = storage.ls.get(PERMISSIONS_KEY)
  return permissions ? JSON.parse(permissions) : []
}

/**
 * 检查当前用户是否有指定权限
 * @param {string|number} permissionId 权限ID
 * @returns {boolean} 是否有权限
 */
export const hasPermission = (permissionId) => {
  if (isSuperAdmin()) return true
  const permissions = getUserPermissions()
  return permissions.includes(String(permissionId))
}

/**
 * 检查当前用户是否有多个权限中的任意一个
 * @param {Array<string|number>} permissionIds 权限ID数组
 * @returns {boolean} 是否有任意权限
 */
export const hasAnyPermission = (permissionIds) => {
  if (isSuperAdmin()) return true
  const permissions = getUserPermissions()
  return permissionIds.some(id => permissions.includes(String(id)))
}

/**
 * 检查当前用户是否有多个权限中的所有权限
 * @param {Array<string|number>} permissionIds 权限ID数组
 * @returns {boolean} 是否有所有权限
 */
export const hasAllPermissions = (permissionIds) => {
  if (isSuperAdmin()) return true
  const permissions = getUserPermissions()
  return permissionIds.every(id => permissions.includes(String(id)))
}

/**
 * 检查是否是超级管理员
 * @returns {boolean} 是否是超级管理员
 */
export const isSuperAdmin = () => {
  const userStr = storage.ls.get('user')
  if (userStr) {
    try {
      const user = JSON.parse(userStr)
      if (user && user.level !== undefined && user.level !== null) {
        return Number(user.level) === 0
      }
    } catch (e) {
      // ignore
    }
  }
  const value = storage.ls.get(IS_SUPER_ADMIN_KEY)
  return value === 'true' || value === true
}

/**
 * 获取当前用户的角色ID
 * @returns {number|null} 角色ID
 */
export const getUserRoleId = () => {
  const value = storage.ls.get(ROLE_ID_KEY)
  return value ? parseInt(value) : null
}

/**
 * 加载当前用户的权限信息
 * @returns {Promise<Object>} 权限信息
 */
export const loadUserPermissions = async () => {
  try {
    const res = await getCurrentUserPermits()
    if (res.code === 1) {
      const { permitIds, roleId, isSuperAdmin: superAdmin } = res.data
      storage.ls.set(PERMISSIONS_KEY, JSON.stringify(permitIds || []))
      storage.ls.set(ROLE_ID_KEY, String(roleId || 1))
      storage.ls.set(IS_SUPER_ADMIN_KEY, String(superAdmin || false))
      return res.data
    }
    return null
  } catch (error) {
    console.error('加载用户权限失败:', error)
    return null
  }
}

/**
 * 清除用户权限信息
 */
export const clearUserPermissions = () => {
  storage.ls.remove(PERMISSIONS_KEY)
  storage.ls.remove(ROLE_ID_KEY)
  storage.ls.remove(IS_SUPER_ADMIN_KEY)
}

/**
 * 路由权限检查
 * 根据路由的 meta.permissions 检查当前用户是否有权限访问
 * @param {Object} route 路由对象
 * @returns {boolean} 是否有权限
 */
export const checkRoutePermission = (route) => {
  // 如果没有配置权限，允许访问
  if (!route.meta || !route.meta.permissions) {
    return true
  }
  
  // 超级管理员拥有所有权限
  if (isSuperAdmin()) {
    return true
  }
  
  const requiredPermissions = route.meta.permissions
  if (Array.isArray(requiredPermissions)) {
    // 需要拥有所有指定权限
    return hasAllPermissions(requiredPermissions)
  } else {
    // 单个权限
    return hasPermission(requiredPermissions)
  }
}
